import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Apple, ArrowDown, ArrowUp, Boxes, Download, Laptop, Plus, ShieldAlert, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AssetFormModal } from '../components/AssetFormModal';
import { AssetStatusBadge, DueBadge } from '../components/badges';
import { CheckInDialog, CheckOutDialog } from '../components/checkout-dialogs';
import { ImportAssetsModal } from '../components/ImportAssetsModal';
import { RemoveRecordsDialog, useIsEdvoraStaff } from '../components/remove-records';
import { Button, Card, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Td, Th, cn } from '../components/ui';
import { api, apiUrl } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ASSET_STATUS_LABEL, formatRelative } from '../lib/format';
import { useBuildings, useCategories, useDebouncedValue, useUrlState } from '../lib/hooks';
import type { AssetListItem, AssetStatus, Page } from '../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = { search: '', status: '', categoryId: '', buildingId: '', locationKind: '', assigned: '', linked: '', page: '1', sort: 'assetTag', dir: 'asc' };

function SortHeader({ label, field, sort, dir, onSort }: { label: string; field: string; sort: string; dir: string; onSort(field: string): void }) {
  const active = sort === field;
  return (
    <button onClick={() => onSort(field)} className={cn('inline-flex items-center gap-1 uppercase', active && 'text-slate-800')}>
      {label}
      {active && (dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
    </button>
  );
}

export function AssetsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const categories = useCategories();
  const buildings = useBuildings();
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState<Map<string, AssetListItem>>(new Map());
  // Edvora staff can take the chosen devices out with their history.
  const [removing, setRemoving] = useState(false);
  const edvora = useIsEdvoraStaff();
  const [checkoutAssets, setCheckoutAssets] = useState<AssetListItem[] | null>(null);
  const [checkinAsset, setCheckinAsset] = useState<AssetListItem | null>(null);

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const page = Number(filters.page) || 1;
  const { page: _page, ...filterParams } = filters;
  const query = useQuery({
    queryKey: ['assets', 'list', filters],
    queryFn: () => api.get<Page<AssetListItem>>('/assets', { ...filterParams, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const onSort = (field: string) =>
    setFilters({ sort: field, dir: filters.sort === field && filters.dir === 'asc' ? 'desc' : 'asc' });

  const toggle = (asset: AssetListItem) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(asset.id)) next.delete(asset.id);
      else next.set(asset.id, asset);
      return next;
    });

  const hasFilters = Object.entries(filters).some(([key, value]) => !['page', 'sort', 'dir'].includes(key) && value);
  const canWrite = can('TECHNICIAN');
  const exportParams = {
    search: filters.search,
    status: filters.status,
    categoryId: filters.categoryId,
    buildingId: filters.buildingId,
    locationKind: filters.locationKind,
    assigned: filters.assigned,
    linked: filters.linked,
  };
  const hasWarehouses = buildings.data?.some((building) => building.kind === 'WAREHOUSE');

  return (
    <>
      <PageHeader
        title="Assets"
        description={query.data ? `${query.data.total.toLocaleString()} ${hasFilters ? 'matching ' : ''}assets` : 'Your technology inventory'}
        actions={
          <>
            <a href={apiUrl('/assets/export.csv', exportParams)}>
              <Button icon={<Download />}>Export</Button>
            </a>
            {can('ADMIN') && (
              <Button icon={<Upload />} onClick={() => setImporting(true)}>
                Import
              </Button>
            )}
            {canWrite && (
              <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
                New asset
              </Button>
            )}
          </>
        }
      />

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search tag, serial, name, model, or assignee"
            className="w-full sm:w-72"
          />
          <Select value={filters.status} onChange={(e) => setFilters({ status: e.target.value })} className="w-auto">
            <option value="">All statuses</option>
            <option value="AVAILABLE,CHECKED_OUT,IN_REPAIR">In service</option>
            {(Object.keys(ASSET_STATUS_LABEL) as AssetStatus[]).map((status) => (
              <option key={status} value={status}>
                {ASSET_STATUS_LABEL[status]}
              </option>
            ))}
          </Select>
          <Select value={filters.categoryId} onChange={(e) => setFilters({ categoryId: e.target.value })} className="w-auto">
            <option value="">All categories</option>
            {categories.data?.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
            <option value="none">Uncategorized</option>
          </Select>
          <Select value={filters.buildingId} onChange={(e) => setFilters({ buildingId: e.target.value })} className="w-auto">
            <option value="">All buildings</option>
            {buildings.data?.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </Select>
          {(hasWarehouses || filters.locationKind) && (
            <Select value={filters.locationKind} onChange={(e) => setFilters({ locationKind: e.target.value })} className="w-auto">
              <option value="">On or off campus</option>
              <option value="CAMPUS">On campus</option>
              <option value="WAREHOUSE">In warehouses</option>
            </Select>
          )}
          <Select value={filters.assigned} onChange={(e) => setFilters({ assigned: e.target.value })} className="w-auto">
            <option value="">Assigned or not</option>
            <option value="true">Assigned</option>
            <option value="false">Not assigned</option>
          </Select>
          <Select value={filters.linked} onChange={(e) => setFilters({ linked: e.target.value })} className="w-auto">
            <option value="">Any Google link</option>
            <option value="true">Linked to Google</option>
            <option value="false">Not linked</option>
          </Select>
          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchInput('');
                setFilters({ search: '', status: '', categoryId: '', buildingId: '', locationKind: '', assigned: '', linked: '' });
              }}
            >
              Clear filters
            </Button>
          )}
          {query.isFetching && !query.isLoading && <Spinner className="size-4" />}
        </div>

        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-3 border-b border-brand-100 bg-brand-50/60 px-4 py-2 text-sm">
            <span className="font-medium text-brand-900">{selected.size} selected</span>
            <Button size="sm" variant="primary" onClick={() => setCheckoutAssets([...selected.values()])}>
              Check out selected
            </Button>
            {edvora && (
              <Button size="sm" variant="ghost" icon={<ShieldAlert />} onClick={() => setRemoving(true)}>
                Remove (Edvora)
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Map())}>
              Clear
            </Button>
          </div>
        )}

        {query.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data!.items.length === 0 ? (
          <EmptyState
            icon={<Boxes />}
            title={hasFilters ? 'No assets match these filters' : 'No assets yet'}
            description={hasFilters ? 'Try a different search or clear the filters.' : 'Add your first asset, import a spreadsheet, or connect Google to pull in Chromebooks.'}
            action={!hasFilters && canWrite && <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>New asset</Button>}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  {canWrite && <Th className="w-10" />}
                  <Th>
                    <SortHeader label="Asset tag" field="assetTag" sort={filters.sort} dir={filters.dir} onSort={onSort} />
                  </Th>
                  <Th>
                    <SortHeader label="Name" field="name" sort={filters.sort} dir={filters.dir} onSort={onSort} />
                  </Th>
                  <Th>Category</Th>
                  <Th>
                    <SortHeader label="Status" field="status" sort={filters.sort} dir={filters.dir} onSort={onSort} />
                  </Th>
                  <Th>Assigned to</Th>
                  <Th>Location</Th>
                  <Th>Device check-in</Th>
                  {canWrite && <Th className="text-right">Actions</Th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {query.data!.items.map((asset) => (
                  <tr key={asset.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/assets/${asset.id}`)}>
                    {canWrite && (
                      <Td className="w-10" >
                        <input
                          type="checkbox"
                          aria-label={`Select ${asset.assetTag}`}
                          disabled={asset.status !== 'AVAILABLE'}
                          checked={selected.has(asset.id)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(asset)}
                          className="size-4 rounded border-slate-300 text-brand-600 disabled:opacity-30"
                        />
                      </Td>
                    )}
                    <Td>
                      <Link to={`/assets/${asset.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {asset.assetTag}
                      </Link>
                    </Td>
                    <Td className="max-w-xs">
                      <p className="truncate font-medium text-slate-900">{asset.name}</p>
                      <p className="truncate text-xs text-slate-500">{[asset.model, asset.serialNumber].filter(Boolean).join(' · ') || '—'}</p>
                    </Td>
                    <Td className="whitespace-nowrap">{asset.category?.name ?? <span className="text-slate-400">—</span>}</Td>
                    <Td>
                      <AssetStatusBadge status={asset.status} />
                    </Td>
                    <Td className="max-w-[14rem]">
                      {asset.activeCheckout ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Link
                            to={
                              asset.activeCheckout.personId
                                ? `/people/${asset.activeCheckout.personId}`
                                : asset.activeCheckout.roomId
                                  ? `/locations/rooms/${asset.activeCheckout.roomId}`
                                  : `/checkouts`
                            }
                            className="block truncate text-slate-800 hover:text-brand-700"
                          >
                            {asset.activeCheckout.assigneeLabel}
                          </Link>
                          <div className="text-xs">
                            <DueBadge dueAt={asset.activeCheckout.dueAt} />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Td>
                    <Td className="max-w-[12rem] text-slate-600">
                      <p className="truncate">{[asset.building?.name, asset.room?.name].filter(Boolean).join(' · ') || <span className="text-slate-400">—</span>}</p>
                      {(asset.cart || asset.building?.kind === 'WAREHOUSE') && (
                        <p className="truncate text-xs text-slate-500">{[asset.cart?.name, asset.building?.kind === 'WAREHOUSE' && 'Off campus'].filter(Boolean).join(' · ')}</p>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {asset.googleDevice ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" title={asset.googleDevice.mostRecentUser ?? undefined}>
                          <Laptop className="size-3.5 text-brand-600" />
                          {formatRelative(asset.googleDevice.lastSync)}
                        </span>
                      ) : asset.managedDevice?.mdmProvider && !asset.managedDevice.mdmRemovedAt ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" title={asset.managedDevice.assignedUserName ?? undefined}>
                          <Apple className="size-3.5 text-brand-600" />
                          {formatRelative(asset.managedDevice.lastCheckInAt)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </Td>
                    {canWrite && (
                      <Td className="text-right whitespace-nowrap">
                        <div onClick={(e) => e.stopPropagation()}>
                          {asset.status === 'AVAILABLE' && (
                            <Button size="sm" variant="subtle" onClick={() => setCheckoutAssets([asset])}>
                              Check out
                            </Button>
                          )}
                          {asset.status === 'CHECKED_OUT' && asset.activeCheckout && (
                            <Button size="sm" onClick={() => setCheckinAsset(asset)}>
                              Check in
                            </Button>
                          )}
                        </div>
                      </Td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={query.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <RemoveRecordsDialog
        open={removing}
        onClose={() => setRemoving(false)}
        subjects={{ assetIds: [...selected.keys()] }}
        onRemoved={() => setSelected(new Map())}
      />
      <AssetFormModal open={creating} onClose={() => setCreating(false)} onSaved={(asset) => navigate(`/assets/${asset.id}`)} />
      <ImportAssetsModal open={importing} onClose={() => setImporting(false)} />
      <CheckOutDialog
        open={!!checkoutAssets}
        onClose={() => setCheckoutAssets(null)}
        assets={checkoutAssets ?? []}
        onDone={() => setSelected(new Map())}
      />
      {checkinAsset?.activeCheckout && (
        <CheckInDialog
          open
          onClose={() => setCheckinAsset(null)}
          checkout={{ ...checkinAsset.activeCheckout, asset: { id: checkinAsset.id, assetTag: checkinAsset.assetTag, name: checkinAsset.name } }}
        />
      )}
    </>
  );
}
